<template>
  <v-container class="psy-forum">
    <h1 class="psy-forum__title">Форум: питання клієнтів</h1>
    <p class="psy-forum__hint">
      Відповідайте на питання клієнтів — відповідь з вашим фото й посиланням на анкету з'явиться на
      <a :href="siteForum" target="_blank" rel="noopener">форумі сайту</a>. На одне питання ви даєте одну відповідь і можете її змінити.
    </p>

    <v-tabs v-model="tab" background-color="primary" dark class="mt-4">
      <v-tab v-for="item in tabs" :key="item.value">
        {{ item.label }}<span v-if="item.value === 'mine' && unread" class="psy-forum__tab-count">{{ unread }}</span>
      </v-tab>
    </v-tabs>

    <div class="d-flex flex-wrap psy-forum__filters mt-4">
      <v-select
        v-model="category"
        :items="categoryItems"
        item-text="name"
        item-value="slug"
        label="Тема"
        outlined
        dense
        hide-details
        class="psy-forum__select"
      ></v-select>
      <v-text-field
        v-model="searchInput"
        label="Пошук"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="psy-forum__search"
      ></v-text-field>
    </div>

    <div v-if="items.length && !loading" class="d-flex justify-end mt-3">
      <v-btn small text color="primary" @click="toggleAll">
        <v-icon small left>{{ allOpen ? "mdi-unfold-less-horizontal" : "mdi-unfold-more-horizontal" }}</v-icon>
        {{ allOpen ? "Згорнути всі" : "Розгорнути всі" }}
      </v-btn>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>
    <p v-else-if="!items.length" class="text-center grey--text py-10">{{ emptyText }}</p>

    <v-card v-for="item in items" :key="item.id" outlined class="psy-forum__card mt-3" :class="{ 'psy-forum__card--open': isOpen(item) }">
      <!-- the question folds into one line: title, topic and whether I answered -->
      <div class="psy-forum__head" role="button" tabindex="0" @click="toggle(item)" @keydown.enter="toggle(item)">
        <div class="psy-forum__head-main">
          <div class="d-flex flex-wrap align-center psy-forum__meta">
            <v-chip v-if="item.category" x-small>{{ item.category.name }}</v-chip>
            <span>{{ item.author_name }} · {{ dateOf(item.created_at) }}</span>
            <span>· відповідей: {{ item.answers_count }}</span>
            <span v-if="item.my_answer" class="psy-forum__answered">· ви відповіли</span>
            <span v-if="item.my_answer && item.my_answer.new_replies" class="psy-forum__new">· нові уточнення: {{ item.my_answer.new_replies }}</span>
          </div>
          <h3 class="psy-forum__question">{{ item.title }}</h3>
        </div>
        <v-icon class="psy-forum__chevron">{{ isOpen(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </div>

      <template v-if="isOpen(item)">
      <p class="psy-forum__body">{{ item.body }}</p>
      <a :href="siteForum + '/q/' + item.path" target="_blank" rel="noopener" class="psy-forum__site">Відкрити на сайті</a>

      <template v-if="editing[item.id] !== undefined">
        <v-textarea
          v-model="editing[item.id]"
          label="Ваша відповідь"
          class="psy-forum__input"
          outlined
          rows="6"
          counter="5000"
          maxlength="5000"
          hint="Щонайменше 20 символів. Відповідь бачать усі відвідувачі сайту."
          persistent-hint
        ></v-textarea>
        <div class="d-flex flex-wrap psy-forum__actions mt-2">
          <v-btn color="primary" depressed :loading="saving === item.id" @click="save(item)">Опублікувати</v-btn>
          <v-btn text @click="cancel(item)">Скасувати</v-btn>
        </div>
      </template>
      <template v-else-if="item.my_answer">
        <div class="psy-forum__mine">
          <div class="psy-forum__mine-label">
            Ваша відповідь<span v-if="item.my_answer.status === 'hidden'" class="red--text"> (прихована адміністратором)</span>:
          </div>
          <p class="mb-0">{{ item.my_answer.body }}</p>
        </div>
        <div class="d-flex flex-wrap psy-forum__actions mt-2">
          <v-btn small outlined color="primary" @click="edit(item)">Змінити</v-btn>
          <v-btn small text color="red" :loading="saving === item.id" @click="remove(item)">Видалити відповідь</v-btn>
        </div>

        <!-- the client's clarifying questions under my answer and my replies -->
        <div v-if="item.my_answer.replies && item.my_answer.replies.length" class="psy-forum__thread">
          <div class="psy-forum__thread-title">Уточнення від автора питання</div>
          <div v-for="reply in item.my_answer.replies" :key="reply.id" class="psy-forum__reply">
            <div class="d-flex flex-wrap align-center psy-forum__reply-head">
              <span class="psy-forum__reply-name">{{ reply.from === "author" ? item.author_name : "Ви" }}</span>
              <span>{{ timeOf(reply.created_at) }}</span>
              <span v-if="reply.is_new" class="psy-forum__new">нове</span>
            </div>
            <p class="mb-0 psy-forum__reply-body">{{ reply.body }}</p>
          </div>
          <template v-if="item.my_answer.status === 'published'">
            <v-textarea
              v-model="replies[item.id]"
              class="psy-forum__input mt-3"
              label="Відповісти на уточнення"
              hint="Бачать автор питання і відвідувачі форуму"
              outlined
              dense
              rows="3"
              counter="5000"
              maxlength="5000"
            ></v-textarea>
            <v-btn color="primary" depressed small :loading="replying === item.id" @click="sendReply(item)">Надіслати</v-btn>
          </template>
        </div>
      </template>
      <v-btn v-else color="primary" depressed class="mt-1" @click="edit(item)">Відповісти</v-btn>
      </template>
    </v-card>

    <v-pagination
      v-if="lastPage > 1"
      v-model="page"
      class="mt-6"
      :length="lastPage"
      :total-visible="7"
    ></v-pagination>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

export default {
  name: "PsychologistForum",
  metaInfo() {
    return { title: "Платформа / Форум" };
  },
  data() {
    return {
      tabs: [
        { value: "todo", label: "Без моєї відповіді" },
        { value: "mine", label: "Мої відповіді" },
        { value: "all", label: "Усі питання" },
      ],
      tab: 0,
      category: "",
      categories: [],
      searchInput: "",
      search: "",
      page: 1,
      lastPage: 1,
      items: [],
      loading: false,
      saving: null,
      editing: {},
      replies: {},
      replying: null,
      unread: 0,
      open: {},
      siteForum: process.env.VUE_APP_MAIN_PAGE + "/forum",
    };
  },
  computed: {
    filter() {
      return this.tabs[this.tab].value;
    },
    categoryItems() {
      return [{ name: "Усі теми", slug: "" }, ...this.categories];
    },
    allOpen() {
      return this.items.length > 0 && this.items.every((item) => this.isOpen(item));
    },
    emptyText() {
      return this.filter === "todo" ? "Нових питань немає — ви відповіли на всі." : "Питань немає.";
    },
  },
  watch: {
    tab() {
      this.reload();
    },
    category() {
      this.reload();
    },
    page() {
      this.load();
    },
    searchInput(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.search = (value || "").trim();
        this.reload();
      }, 400);
    },
  },
  mounted() {
    ApiClient.get("api/forum/categories").then((response) => {
      this.categories = response.data.data;
    });
    // new clarifying questions: open straight on "Мої відповіді", they are listed first there
    ApiClient.get("api/psychologists/me/forum/unread")
      .then((response) => {
        this.unread = response.data.data.count || 0;
      })
      .catch(() => {})
      .finally(() => {
        if (this.unread && this.tab === 0) {
          this.tab = 1;
        } else {
          this.load();
        }
      });
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    reload() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.load();
      }
    },
    load() {
      const params = new URLSearchParams({ page: this.page, filter: this.filter });
      if (this.category) params.append("category", this.category);
      if (this.search) params.append("search", this.search);
      this.loading = true;
      return ApiClient.get("api/psychologists/me/forum/questions?" + params.toString())
        .then((response) => {
          this.items = response.data.data;
          this.lastPage = response.data.meta.last_page || 1;
          this.unread = response.data.meta.unread || 0;
          this.editing = {};
          this.open = {};
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isOpen(item) {
      return !!this.open[item.id] || this.editing[item.id] !== undefined;
    },
    toggle(item) {
      if (this.editing[item.id] !== undefined) return;
      Vue.set(this.open, item.id, !this.open[item.id]);
      if (this.open[item.id]) this.markSeen(item);
    },
    toggleAll() {
      const value = !this.allOpen;
      const open = {};
      this.items.forEach((item) => {
        open[item.id] = value;
        if (value) this.markSeen(item);
      });
      this.open = open;
    },
    // opened, the client's new messages are read (the "нове" marks stay until reload)
    markSeen(item) {
      if (!item.my_answer || !item.my_answer.new_replies) return;
      ApiClient.post(`api/psychologists/me/forum/questions/${item.id}/answer/seen`)
        .then(() => {
          item.my_answer.new_replies = 0;
          this.unread = Math.max(0, this.unread - 1);
          this.$root.$emit("menu-counters-refresh");
        })
        .catch(() => {});
    },
    sendReply(item) {
      const body = (this.replies[item.id] || "").trim();
      if (body.length < 2) {
        this.toast("Напишіть відповідь", "error");
        return;
      }
      this.replying = item.id;
      ApiClient.post(`api/psychologists/me/forum/questions/${item.id}/answer/replies`, { body })
        .then((response) => {
          if (!response) return;
          item.my_answer.replies.push(response.data.data);
          Vue.set(this.replies, item.id, "");
          this.toast(response.data.message);
        })
        .catch(() => {
          this.toast("Не вдалося надіслати. Спробуйте пізніше", "error");
        })
        .finally(() => {
          this.replying = null;
        });
    },
    edit(item) {
      Vue.set(this.open, item.id, true);
      Vue.set(this.editing, item.id, item.my_answer ? item.my_answer.body : "");
    },
    cancel(item) {
      Vue.delete(this.editing, item.id);
    },
    save(item) {
      const body = (this.editing[item.id] || "").trim();
      if (body.length < 20) {
        this.toast("Відповідь має містити щонайменше 20 символів", "error");
        return;
      }
      this.saving = item.id;
      ApiClient.post(`api/psychologists/me/forum/questions/${item.id}/answer`, { body })
        .then((response) => {
          if (!response) return;
          const isNew = !item.my_answer;
          // the save returns the answer without its dialog: keep the one already shown
          item.my_answer = { replies: [], new_replies: 0, ...item.my_answer, ...response.data.data };
          if (isNew) item.answers_count++;
          Vue.delete(this.editing, item.id);
          this.toast(response.data.message);
        })
        .finally(() => {
          this.saving = null;
        });
    },
    remove(item) {
      this.saving = item.id;
      ApiClient.delete(`api/psychologists/me/forum/questions/${item.id}/answer`)
        .then((response) => {
          item.my_answer = null;
          item.answers_count = Math.max(0, item.answers_count - 1);
          this.toast(response.data.message);
        })
        .finally(() => {
          this.saving = null;
        });
    },
    dateOf(value) {
      return new Date(value).toLocaleDateString("uk-UA", { day: "numeric", month: "long" });
    },
    timeOf(value) {
      return new Date(value).toLocaleString("uk-UA", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
    },
    toast(text, icon = "success") {
      Vue.swal({
        target: document.getElementById("main"),
        text,
        icon,
        timer: 3000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.psy-forum {
  padding-top: 32px;
  padding-bottom: 60px;
}
.psy-forum__title {
  font-size: 26px;
  font-weight: 500;
}
.psy-forum__hint {
  margin-top: 8px;
  color: #575756;
}
.psy-forum__filters {
  gap: 12px;
}
.psy-forum__select {
  flex: 0 1 260px;
}
.psy-forum__search {
  flex: 1 1 260px;
}
.psy-forum__card {
  padding: 12px 18px;
  border-radius: 10px !important;
  &--open {
    padding-bottom: 18px;
    border-color: #00a3b7 !important;
  }
}
.psy-forum__head {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  outline: none;
}
.psy-forum__head-main {
  flex: 1 1 auto;
  min-width: 0;
}
.psy-forum__chevron {
  flex: none;
}
.psy-forum__answered {
  color: #2e9e5b;
}
.psy-forum__site {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 14px;
  text-decoration: none;
}
.psy-forum__meta {
  gap: 8px;
  color: #868686;
  font-size: 14px;
}
.psy-forum__question {
  margin-top: 6px;
  overflow-wrap: break-word;
  color: #00a3b7;
  font-size: 18px;
  font-weight: 500;
}
.psy-forum__body {
  margin: 12px 0 8px;
  line-height: 1.45;
  white-space: pre-line;
}
.psy-forum__mine {
  padding: 12px 14px;
  background: #f4f8fa;
  border-radius: 8px;
  white-space: pre-line;
}
.psy-forum__mine-label {
  margin-bottom: 4px;
  font-weight: 500;
}
.psy-forum__actions {
  gap: 8px;
}
.psy-forum__tab-count {
  margin-left: 6px;
  padding: 0 7px;
  background: #E5484D;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
}
.psy-forum__new {
  color: #E5484D;
  font-weight: 500;
}
// a fixed height with its own scroll instead of auto-grow (see the site's AskForm)
.psy-forum__input ::v-deep textarea {
  overflow-y: auto;
  resize: vertical;
  max-height: 50vh;
}
// the dialog under my answer, a level lower
.psy-forum__thread {
  margin: 16px 0 0 24px;
  padding-left: 14px;
  border-left: 2px solid #D9E4EB;
}
.psy-forum__thread-title {
  margin-bottom: 8px;
  font-weight: 500;
}
.psy-forum__reply + .psy-forum__reply {
  margin-top: 12px;
}
.psy-forum__reply-head {
  gap: 4px 10px;
  color: #868686;
  font-size: 13px;
}
.psy-forum__reply-name {
  color: #575756;
  font-weight: 500;
}
.psy-forum__reply-body {
  white-space: pre-line;
  overflow-wrap: break-word;
}
@media (max-width: 600px) {
  .psy-forum__thread {
    margin-left: 0;
    padding-left: 10px;
  }
}
</style>
